import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Home,
  Mountain,
  ShieldCheck,
  School,
  Snowflake,
  Wind,
  MapPin,
  Phone,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { SpringsOfficeMap } from "@/components/SpringsOfficeMap";
import { AiAnswerBlock } from "@/components/geo/AiAnswerBlock";
import { QuotableStat } from "@/components/geo/QuotableStat";
import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";

const SLUG = "moving-to-colorado-springs";
const CANONICAL = `${SITE_URL}/${SLUG}`;
const DATE_PUBLISHED = "2026-05-24";

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "What's the best neighborhood in Colorado Springs for military families?",
    a: "For Fort Carson, the easiest commutes are from Fountain, Security-Widefield, Stratmoor, and the southern Broadmoor area — most are 10–20 minutes to the gate. For Peterson SFB and Schriever SFB, Powers Corridor, Cimarron Hills, Falcon, and Meridian Ranch put you closest to base and to good schools. USAFA families tend to land in Northgate, Flying Horse, Briargate, and Monument.",
  },
  {
    q: "What's the cost of living in Colorado Springs vs. Denver?",
    a: "Colorado Springs is generally 10–20% cheaper than Denver on housing, with property taxes that are noticeably lower than most Front Range metros. Groceries, fuel, and services are similar. Utilities run a bit higher in winter because of altitude and gas-heat reliance, but that's offset by lower cooling costs in summer.",
  },
  {
    q: "How much snow does Colorado Springs actually get?",
    a: "The city averages around 40 inches of snow per year, but it melts fast — most storms are cleared within 24–48 hours because of the high sun angle. The west side and Monument Hill get more snow than the east side and Fountain, which often miss storms entirely. Plan for snow tires November–April if you're north of I-25, especially in Black Forest, Monument, or Woodland Park.",
  },
  {
    q: "How bad is the altitude adjustment in Colorado Springs?",
    a: "Colorado Springs sits at about 6,035 feet — roughly 800 feet higher than Denver. Most people feel mild altitude effects (lighter sleep, easier dehydration, faster breath on exertion) for the first 1–3 weeks. Drink more water than you think you need, hold off on intense workouts for a week, and limit alcohol on day one. Most acclimate fully by week three.",
  },
  {
    q: "Which school district is best in Colorado Springs?",
    a: "Districts vary by neighborhood. Academy District 20 (north — Briargate, Northgate, Flying Horse) is consistently top-ranked. Cheyenne Mountain D-12 (Broadmoor / southwest) is small and high-performing. Lewis-Palmer D-38 (Monument) is also highly rated. Districts 49 (Falcon / Stetson Hills / Cimarron Hills) and 11 (central Springs) are larger and more variable — research specific schools rather than the district as a whole.",
  },
  {
    q: "Do I need hail insurance in Colorado Springs?",
    a: "Yes. The Pikes Peak region averages multiple major hail events every May through September, and roof replacements driven by hail are routine. Make sure your homeowners policy uses replacement cost (not actual cash value) for your roof, and ask whether the deductible is a flat dollar amount or a percentage of dwelling coverage — that one detail can swing your out-of-pocket by thousands.",
  },
  {
    q: "What about wildfires?",
    a: "Wildfire risk is real in the wildland-urban interface — Black Forest, Cedar Heights, parts of the Broadmoor, and most of Teller County (Woodland Park, Divide, Florissant). If you're moving into one of those areas, ask your insurance agent specifically about wildfire coverage and look up the property's WUI rating. In the urban grid (Powers, Briargate, downtown), wildfire risk is significantly lower.",
  },
  {
    q: "How is the traffic in Colorado Springs?",
    a: "Light by Front Range standards. The worst stretches are I-25 north-south during commute hours (especially the I-25 / Cimarron interchange and the Monument hill), Powers Boulevard around Briargate, and Woodmen Road through Northgate. Most local trips stay under 25 minutes. Getting to Denver is 70 miles up I-25 and can take 90 minutes to 2 hours in PCS season or ski Fridays.",
  },
  {
    q: "Where do dog owners live in Colorado Springs?",
    a: "Bear Creek Dog Park (southwest), Palmer Park (central), and the Bluffs (Briargate) are the most-loved off-leash spots. Garden of the Gods is leashed only. Black Forest, Falcon, and Monument have acreage lots that work well for big dogs. Most apartment complexes in Powers and Briargate are dog-friendly with pet deposits.",
  },
  {
    q: "What internet options are available in Colorado Springs?",
    a: "Xfinity (Comcast) covers most of the metro, Quantum Fiber / CenturyLink has fiber in many newer subdivisions (Banning Lewis, parts of Briargate, Flying Horse, Wolf Ranch, and growing east-side coverage), and T-Mobile / Verizon home internet works well in most areas. Rural addresses (Black Forest, Peyton, Calhan, Ellicott) often rely on fixed wireless or Starlink.",
  },
  {
    q: "What's the job market like in Colorado Springs?",
    a: "Heavily weighted to defense (Fort Carson, Peterson SFB, Schriever SFB, USAFA, NORAD), aerospace and cybersecurity contractors, healthcare (UCHealth, Centura, Children's Hospital Colorado), and tourism. The tech and remote-work scene has grown a lot since 2020, and small-business density is high — trades, healthcare, and pro-services especially.",
  },
  {
    q: "What are the must-do things after moving to Colorado Springs?",
    a: "Get up Pikes Peak (drive, cog rail, or hike Barr Trail). Hike Garden of the Gods early morning before the crowds. Walk the Manitou Incline if you're ambitious. Visit the Air Force Academy, the Olympic & Paralympic Museum, and Cheyenne Mountain Zoo. Spend a weekend in Woodland Park, eat downtown, and take a day trip to Cripple Creek or Cañon City.",
  },
];

const NEIGHBORHOODS: Array<{ name: string; bestFor: string; note: string }> = [
  { name: "Briargate", bestFor: "Top-ranked schools, north-end family living", note: "Academy District 20, close to Costco / dining, easy USAFA + Peterson commute." },
  { name: "Northgate / Flying Horse", bestFor: "Newer construction, golf-course living", note: "I-25 access, top schools, growing dining and retail along Voyager." },
  { name: "Powers Corridor", bestFor: "Convenience, retail, mid-range housing", note: "Highest 'near me' search density in the city — every service business is here." },
  { name: "Old Colorado City / Westside", bestFor: "Historic charm, walkable downtown feel", note: "Older homes, eclectic small businesses, close to Manitou and Garden of the Gods." },
  { name: "Broadmoor", bestFor: "Luxury, prestige, quiet streets", note: "Cheyenne Mountain D-12 schools, walkable to The Broadmoor resort, close to Cheyenne Cañon trails." },
  { name: "Black Forest", bestFor: "Acreage, privacy, mountain feel", note: "Big lots, wells/septic, wildfire WUI — beautiful but plan accordingly." },
  { name: "Monument / Tri-Lakes", bestFor: "Quiet, top schools, halfway to Denver", note: "Lewis-Palmer D-38, easy I-25 access, more snow than the rest of the metro." },
  { name: "Falcon / Meridian Ranch", bestFor: "Newer builds, value, Schriever / Peterson commutes", note: "Master-planned subdivisions, growing fast, good schools in D-49." },
  { name: "Fountain / Security-Widefield", bestFor: "Fort Carson families, affordability", note: "10–15 minutes to Fort Carson gate, more home for the money, mix of older and new builds." },
  { name: "Stetson Hills / Cimarron Hills", bestFor: "Mid-range east-side family living", note: "Easy access to Powers and Peterson, mix of school districts depending on street." },
  { name: "Woodland Park", bestFor: "Mountain living, 25 minutes up Hwy 24", note: "Cooler summers, more snow, Teller County, small-town feel with Springs nearby." },
];

const BASES: Array<{ name: string; commute: string; notes: string }> = [
  {
    name: "Fort Carson",
    commute: "South Springs — gate access from Hwy 115 and Academy Blvd",
    notes: "Best off-base options: Fountain, Security-Widefield, Stratmoor, southwest Broadmoor side streets, and parts of Cheyenne Meadows. On-base housing waitlists vary by rank and bedroom count.",
  },
  {
    name: "Peterson Space Force Base",
    commute: "East Springs near the airport — gate access off Powers and Galley",
    notes: "Powers Corridor, Stetson Hills, Cimarron Hills, and Falcon all put you within 15–25 minutes. Briargate is doable if schools matter more than commute.",
  },
  {
    name: "Schriever Space Force Base",
    commute: "Far east of Peterson — about 25 minutes from Powers",
    notes: "Falcon, Meridian Ranch, Woodmen Hills, and Peyton are the natural fit. Some Schriever families also choose Ellicott or Calhan for true country living.",
  },
  {
    name: "USAFA (Air Force Academy)",
    commute: "North Springs along I-25 — north and south gates",
    notes: "Northgate, Flying Horse, Briargate, and Monument are the dominant choices. Academy D-20 and Lewis-Palmer D-38 schools, fast highway access.",
  },
];

const LOCAL_SERVICES: Array<{ to: string; label: string }> = [
  { to: "/hvac-marketing-colorado", label: "HVAC contractors — Colorado Springs" },
  { to: "/plumbing-marketing-colorado", label: "Plumbers — Colorado Springs" },
  { to: "/roofing-seo-colorado", label: "Roofers — Colorado Springs" },
  { to: "/electrical-contractor-marketing-colorado", label: "Electricians — Colorado Springs" },
  { to: "/landscaping-seo-colorado", label: "Landscaping — Pikes Peak region" },
  { to: "/dental-marketing-colorado", label: "Dental practices — Colorado Springs" },
  { to: "/colorado-springs-web-design", label: "Local business web design" },
  { to: "/colorado-springs-seo", label: "Local SEO — Colorado Springs" },
];

export const Route = createFileRoute("/moving-to-colorado-springs")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      { title: "Moving to Colorado Springs — Local's Relocation Guide" },
      { name: "description", content: "Honest local's guide to moving to Colorado Springs: neighborhoods for Fort Carson, Peterson SFB, USAFA families, schools, altitude, snow, hail, and key services." },
      { property: "og:title", content: "Moving to Colorado Springs — The Local's Guide" },
      {
        property: "og:description",
        content:
          "Neighborhoods, bases, schools, weather, and the local services you'll need. Written by a real Colorado Springs business owner — not relocation-site fluff.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Moving to Colorado Springs — Local's Guide" },
      {
        name: "twitter:description",
        content: "Neighborhoods, bases, schools, weather, services — the honest local version.",
      },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `${CANONICAL}#article`,
              headline:
                "Moving to Colorado Springs: The Local's Guide to Neighborhoods, Bases & Services",
              description:
                "Honest local's guide to moving to Colorado Springs — neighborhoods, military bases, schools, weather, and local services.",
              datePublished: DATE_PUBLISHED,
              dateModified: DATE_PUBLISHED,
              image: `${SITE_URL}/logo.png`,
              author: { "@id": SCHEMA_PERSON_ID },
              publisher: { "@id": SCHEMA_ORG_ID },
              mainEntityOfPage: { "@type": "WebPage", "@id": CANONICAL },
              about: { "@id": SCHEMA_LOCALBUSINESS_ID },
            },
            {
              "@type": "FAQPage",
              "@id": `${CANONICAL}#faq`,
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${CANONICAL}#breadcrumbs`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Colorado Springs",
                  item: `${SITE_URL}/colorado-springs`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Moving to Colorado Springs",
                  item: CANONICAL,
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: MovingToColoradoSpringsPage,
});

function MovingToColoradoSpringsPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Local's Guide"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Colorado Springs", to: "/colorado-springs" },
          { label: "Moving to Colorado Springs" },
        ]}
        title={
          <>
            Moving to <span className="text-gradient-gold">Colorado Springs</span>:
            The Local's Guide
          </>
        }
        description="Neighborhoods, military bases, schools, weather, and the local services you'll actually need — written by a Colorado Springs business owner, not a relocation site."
      />

      {/* Quick answer for AI engines */}
      <section className="bg-background pt-12">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <AiAnswerBlock
            question="Where should I live in Colorado Springs?"
            answer="It depends on where you work and what you need from schools. North Springs (Briargate, Northgate, Flying Horse, Monument) has the highest-ranked schools and easy USAFA / Peterson commutes. East Springs (Powers, Cimarron Hills, Falcon, Meridian Ranch) is convenient to Peterson and Schriever with newer housing. South Springs (Fountain, Security-Widefield, Stratmoor) is closest to Fort Carson and the most affordable. The Broadmoor and Old Colorado City are the quiet, character options."
            bullets={[
              "Fort Carson families: Fountain, Security-Widefield, Stratmoor, southern Broadmoor.",
              "Peterson SFB / Schriever SFB families: Powers, Cimarron Hills, Falcon, Meridian Ranch.",
              "USAFA families: Northgate, Flying Horse, Briargate, Monument.",
              "Quiet / luxury / character: Broadmoor, Old Colorado City, Black Forest, Woodland Park.",
            ]}
          />
          <AiAnswerBlock
            question="What's the best neighborhood for Fort Carson families?"
            answer="Fountain and Security-Widefield are the most popular choices — 10 to 20 minutes to the Fort Carson gate, more home for the money, and a strong military community. Stratmoor and the southern side streets near Broadmoor are quieter alternatives that still keep the commute under 20 minutes. On-base housing is an option if waitlists work for your timeline and bedroom count."
          />
        </div>
      </section>

      {/* Quotable stats for AI Overviews */}
      <section className="bg-background">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <QuotableStat
            stat="Colorado Springs sits at 6,035 feet — about 800 feet higher than Denver."
            context="Most newcomers feel mild altitude effects for the first 1–3 weeks. Drink more water than you think you need, ease back on hard workouts and alcohol the first few days, and most people fully acclimate by week three."
          />
          <QuotableStat
            stat="The Pikes Peak region averages multiple major hail events every May through September."
            context="Hail-driven roof replacements are routine here. Verify your homeowners policy uses replacement cost (not actual cash value) for the roof, and check whether your deductible is a flat dollar amount or a percentage of dwelling coverage."
          />
          <QuotableStat
            stat="Colorado Springs has four major military installations: Fort Carson, Peterson SFB, Schriever SFB, and the USAF Academy."
            context="Each base pulls demand into different parts of the city — and that, more than anything else, shapes neighborhood pricing, school demand, and PCS-season rental availability from May through August."
          />
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="bg-section-alt py-16 lg:py-20" aria-labelledby="neighborhoods-heading">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Neighborhoods
            </span>
            <h2
              id="neighborhoods-heading"
              className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl"
            >
              Where Colorado Springs locals actually live
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Honest neighborhood breakdown — what each area is best for, what you give up, and what
              the locals quietly know.
            </p>
          </div>

          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {NEIGHBORHOODS.map((n) => (
              <li key={n.name} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="flex items-center gap-2 text-brand-gold-deep">
                  <Home className="h-4 w-4" aria-hidden="true" />
                  <h3 className="text-lg font-extrabold text-foreground">{n.name}</h3>
                </div>
                <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
                  {n.bestFor}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{n.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Military bases */}
      <section className="bg-background py-16 lg:py-20" aria-labelledby="bases-heading">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              PCS / Military
            </span>
            <h2
              id="bases-heading"
              className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl"
            >
              Base-by-base relocation notes
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Where to live by base — commutes, off-base options, and school feeder notes.
            </p>
          </div>

          <ul className="mt-10 space-y-5">
            {BASES.map((b) => (
              <li
                key={b.name}
                className="rounded-2xl border border-border bg-card p-6 shadow-card sm:p-7"
              >
                <div className="flex items-center gap-2 text-brand-gold-deep">
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  <h3 className="text-xl font-extrabold text-foreground">{b.name}</h3>
                </div>
                <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  {b.commute}
                </p>
                <p className="mt-3 text-base leading-relaxed text-foreground/85">{b.notes}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Weather / altitude / hail callouts */}
      <section className="bg-section-alt py-16 lg:py-20" aria-labelledby="climate-heading">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Climate & altitude
            </span>
            <h2
              id="climate-heading"
              className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl"
            >
              What the weather is actually like
            </h2>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {[
              { Icon: Mountain, title: "Moving Your Business to Colorado Springs Guide", body: "Most people acclimate in 1–3 weeks. Hydrate, ease into hard workouts, go easy on alcohol day one." },
              { Icon: Snowflake, title: "Snow (~40 in / yr)", body: "Melts fast thanks to the high sun angle. Monument and the west side get more; Fountain and the east side often miss storms." },
              { Icon: Wind, title: "Hail (May–September)", body: "Multiple major events per year. Use replacement-cost roof coverage and know your deductible structure." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="flex items-center gap-2 text-brand-gold-deep">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  <h3 className="text-lg font-extrabold text-foreground">{title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schools quick reference */}
      <section className="bg-background py-16 lg:py-20" aria-labelledby="schools-heading">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Schools
            </span>
            <h2
              id="schools-heading"
              className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl"
            >
              School districts at a glance
            </h2>
          </div>

          <ul className="mt-10 grid gap-5 sm:grid-cols-2">
            {[
              { name: "Academy District 20", area: "Briargate, Northgate, Flying Horse, north Powers", note: "Consistently top-ranked in the metro." },
              { name: "Lewis-Palmer D-38", area: "Monument, Tri-Lakes", note: "Highly rated, smaller district, family-focused." },
              { name: "Cheyenne Mountain D-12", area: "Broadmoor, southwest Springs", note: "Small, high-performing, tight community." },
              { name: "District 49", area: "Falcon, Stetson Hills, Cimarron Hills, Meridian Ranch", note: "Big, fast-growing — research individual schools." },
              { name: "District 11", area: "Central / older Springs", note: "Large urban district with strong magnet and charter options." },
              { name: "Fountain-Fort Carson D-8", area: "Fountain, Fort Carson on-base", note: "Strong military-family support and resources." },
            ].map((d) => (
              <li key={d.name} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="flex items-center gap-2 text-brand-gold-deep">
                  <School className="h-4 w-4" aria-hidden="true" />
                  <h3 className="text-lg font-extrabold text-foreground">{d.name}</h3>
                </div>
                <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  {d.area}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground/85">{d.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Local services you'll need */}
      <section className="bg-section-alt py-16 lg:py-20" aria-labelledby="services-heading">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Local services
            </span>
            <h2
              id="services-heading"
              className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl"
            >
              Local services you'll need after you move in
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              HVAC tune-ups, hail-season roof inspections, irrigation startup, a dentist, a
              plumber — and if you're a local business owner reading this, the SEO and websites
              that make people pick you.
            </p>
          </div>

          <ul className="mt-10 grid gap-3 sm:grid-cols-2">
            {LOCAL_SERVICES.map((s) => (
              <li key={s.to}>
                <Link
                  to={s.to}
                  className="flex items-center justify-between rounded-xl border border-border bg-card p-4 text-sm font-semibold text-foreground shadow-card transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                >
                  <span className="inline-flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-brand-gold-deep" aria-hidden="true" />
                    {s.label}
                  </span>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-16 lg:py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              FAQ
            </span>
            <h2 id="faq-heading" className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl">
              Moving to Colorado Springs — frequently asked questions
            </h2>
          </div>

          <dl className="mt-10 space-y-5">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <dt className="text-lg font-extrabold text-foreground">{f.q}</dt>
                <dd className="mt-3 text-base leading-relaxed text-foreground/85">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <SpringsOfficeMap />

      {/* CTA */}
      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Running a business in the Springs?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            If you're a local business owner who wants to actually rank in Colorado Springs — on
            Google, in the Map Pack, and in AI answers — start with our{" "}
            <Link to="/colorado-springs-seo" className="font-semibold text-brand-gold-deep underline-offset-2 hover:underline">
              Colorado Springs SEO page
            </Link>{" "}
            or talk to Chris directly.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a free Colorado Springs proposal
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
